import { useEffect, useState } from 'react';
import type { ListaTablets } from '../../api/operaciones';
import type { CodigoEmparejar, Tablet } from '../../api/ops/equipo';
import { ErrorApi } from '../../api/problema';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { EstadoError, EstadoNoContratado } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Ventana } from '../../componentes/Ventana';
import { estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { haceCuanto } from '../../logica/tiempo';
import { PERMISOS, type ContextoEquipo } from './contexto';
import { esNoContratado, textoDeError } from './textos';

/**
 * Tablets del negocio en el panel (1.13, apartado de 1.9): lista con su sede y su última conexión, emparejar con un código
 * de 6 números que caduca a los 10 minutos (G178) y una sede opcional («Todo el negocio» por defecto, G212), y revocar
 * con ventana (la tablet se cierra al momento, G80). Sin el bloque, `403 entitlement_required` (G222): no contratado.
 */
export function Tablets({ c, cifra, emparejar, onCerrarEmparejar, onNoContratado }: { c: ContextoEquipo; cifra: (n: number) => void; emparejar: boolean; onCerrarEmparejar: () => void; onNoContratado: () => void }) {
    const pag = usePaginacionCursor();
    const [ahora] = useState(() => Date.now());
    const [lista, setLista] = useState<ListaTablets | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [revocar, setRevocar] = useState<Tablet | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const { ops, negocio, avisar } = c;
    const gestionar = c.permisos.puede(PERMISOS.tablets) && !c.soloLectura;

    useEffect(() => {
        let vivo = true;
        ops.listDevices(negocio, { cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                    cifra(l.total_count);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                    if (esNoContratado(e)) {
                        onNoContratado();
                    }
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pag.cursor, intento, cifra, onNoContratado]);

    if (esNoContratado(error)) {
        return (
            <EstadoNoContratado
                bloque="Tablet y escáner"
                incluye={['Abre Sala, escáner y taquilla en una tablet sin que nadie inicie sesión.', 'Cada persona entra con su PIN.']}
                propietario={c.soyPropietario}
                onAnadir={null}
                onInicio={null}
                movil={c.movil}
            />
        );
    }
    if (error !== null && lista === null) {
        return <EstadoError seccion="Tablets" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }

    const hacerRevocar = () => {
        if (revocar === null) {
            return;
        }
        setOcupado(true);
        ops.revokeDevice(negocio, revocar.id)
            .then(() => {
                avisar({ tipo: 'ok', texto: `${revocar.name} revocada.` });
                setRevocar(null);
                setIntento((i) => i + 1);
                c.refrescar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido revocar la tablet. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const nombreSede = (id: string | null | undefined) => (id == null ? null : (c.sedes.find((s) => s.id === id)?.name ?? null));

    return (
        <>
            {lista !== null && lista.data.length === 0 && (
                <div className="rv-eq-vacia">
                    <span className="rv-eq-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>Todavía no hay tablets emparejadas.</strong>
                    <span>Empareja una para abrir Sala, escáner y taquilla con el PIN de cada persona.</span>
                </div>
            )}
            {lista !== null && lista.data.length > 0 && (
                <div className="rv-eq-rejilla rv-eq-rejilla--tablets" role="list" aria-label="Tablets">
                    {lista.data.map((t) => {
                        const conectada = t.last_seen_at != null && ahora - new Date(t.last_seen_at).getTime() < 2 * 60_000;
                        const conexion = conectada ? 'Conectada ahora' : t.last_seen_at == null ? 'Sin conexión todavía' : `Última conexión ${haceCuanto(t.last_seen_at)}`;
                        const sede = nombreSede(t.location_id);
                        const emparejada = new Date(t.paired_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
                        return (
                            <div key={t.id} role="listitem" className="rv-eq-tablet">
                                <span className="rv-eq-tablet__baldosa">
                                    <Icono nombre="tablet-smartphone" />
                                </span>
                                <strong>{t.name}</strong>
                                <span className="rv-eq-tablet__linea">{[conexion, sede ?? 'Todo el negocio', `Emparejada el ${emparejada}`].join(' • ')}</span>
                                <span className="rv-eq-tablet__pie">
                                    {t.status === 'revoked' ? (
                                        <Chip estado="neutral">Revocada</Chip>
                                    ) : (
                                        <>
                                            <span>{conexion}</span>
                                            {gestionar && (
                                                <Boton tipo="secundario" rojo icono="unplug" onClick={() => { setRevocar(t); }}>
                                                    Revocar
                                                </Boton>
                                            )}
                                        </>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <div className="rv-eq-pie">
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
                </div>
            )}
            <Ventana
                abierta={revocar !== null}
                titulo={`¿Revocar ${revocar?.name ?? 'la tablet'}?`}
                texto="La tablet se cierra al momento. Para volver a usarla hay que emparejarla otra vez."
                accion="Revocar"
                accionIcono="unplug"
                ocupado={ocupado}
                ocupadoTexto="Revocando…"
                movil={c.movil}
                onAccion={hacerRevocar}
                onCerrar={() => {
                    setRevocar(null);
                }}
            />
            {emparejar && (
                <VentanaEmparejar
                    c={c}
                    onCerrar={() => {
                        onCerrarEmparejar();
                        setIntento((i) => i + 1);
                        c.refrescar();
                    }}
                    onNoContratado={() => {
                        setError(new ErrorApi({ type: 'https://api.reservaria.app/errors/entitlement_required', title: 'No contratado', status: 403, code: 'entitlement_required' }));
                        onCerrarEmparejar();
                        onNoContratado();
                    }}
                />
            )}
        </>
    );
}

/**
 * Emparejar una tablet: nombre y sede opcional (G212) → código de 6 números con su cuenta atrás de 10 minutos. La tablet
 * lo escribe en `/tablet` (1.13).
 */
function VentanaEmparejar({ c, onCerrar, onNoContratado }: { c: ContextoEquipo; onCerrar: () => void; onNoContratado: () => void }) {
    const [nombre, setNombre] = useState('');
    const [sede, setSede] = useState('');
    const [codigo, setCodigo] = useState<CodigoEmparejar | null>(null);
    const [quedan, setQuedan] = useState(600);
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { ops, negocio } = c;

    useEffect(() => {
        if (codigo === null) {
            return;
        }
        const fin = new Date(codigo.expires_at).getTime();
        const cuenta = () => {
            setQuedan(Math.max(0, Math.round((fin - Date.now()) / 1000)));
        };
        cuenta();
        const t = setInterval(cuenta, 1000);
        return () => {
            clearInterval(t);
        };
    }, [codigo]);

    const pedir = () => {
        if (nombre.trim() === '') {
            setError('Ponle un nombre.');
            return;
        }
        setOcupado(true);
        setError(null);
        ops.createPairingCode(negocio, { name: nombre.trim(), ...(sede === '' ? {} : { location_id: sede }) })
            .then(setCodigo)
            .catch((e: unknown) => {
                if (esNoContratado(e)) {
                    onNoContratado();
                    return;
                }
                setError(textoDeError(e, 'No he podido crear el código. Prueba de nuevo.'));
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const minutos = Math.floor(quedan / 60);
    const segundos = quedan % 60;

    return codigo === null ? (
        <Ventana
            abierta
            titulo="Emparejar una tablet"
            texto="Primero dale un nombre. Luego te damos un código de 6 números para escribirlo en la tablet."
            accion="Ver el código"
            accionIcono="arrow-right"
            ocupado={ocupado}
            ocupadoTexto="Creando…"
            movil={c.movil}
            onAccion={pedir}
            onCerrar={onCerrar}
        >
            <div className="rv-eq-ventana-campos">
                <Campo etiqueta="Nombre de la tablet" valor={nombre} onChange={setNombre} error={error} ayuda="Así la reconoces en la lista. Por ejemplo, dónde está." max={80} nombre="nombre-tablet" onEnter={pedir} />
                {c.sedes.length > 0 && (
                    <label className="rv-eq-select">
                        <span>Sede</span>
                        <select
                            value={sede}
                            onChange={(e) => {
                                setSede(e.target.value);
                            }}
                        >
                            <option value="">Todo el negocio</option>
                            {c.sedes.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                        <Icono nombre="chevron-down" />
                    </label>
                )}
            </div>
        </Ventana>
    ) : (
        <Ventana
            abierta
            titulo="Escribe este código en la tablet"
            // La dirección es la del propio panel (el panel no conoce dominios, G118).
            texto={`Abre ${typeof window === 'undefined' ? '' : window.location.host}/tablet en la tablet y escribe estos 6 números. El código caduca a los 10 minutos.`}
            cancelar="Cerrar"
            accion=""
            movil={c.movil}
            onCerrar={onCerrar}
        >
            <div className="rv-eq-codigo" aria-live="polite">
                <strong aria-label={`Código ${codigo.code.split('').join(' ')}`}>{codigo.code}</strong>
                <span>{quedan > 0 ? `Caduca en ${String(minutos)}:${String(segundos).padStart(2, '0')}` : 'Ha caducado. Cierra y crea otro.'}</span>
            </div>
        </Ventana>
    );
}
